import React, { type ReactNode, type Ref, useCallback, forwardRef } from 'react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';

// If you add a new action type, please update the `actionType` enum in Data Portal, so we can monitor usage
// https://data-portal.internal.atlassian.com/analytics/registry/66149?tabIndex=Attributes
type ActionType =
	| 'sortAsc'
	| 'sortDesc'
	| 'clearSorting'
	| 'moveColumnFirst'
	| 'moveColumnLeft'
	| 'moveColumnRight'
	| 'moveColumnLast'
	| 'removeColumn'
	| 'resizeColumn'
	| 'resetColumnWidth'
	| 'editField';

type Props = {
	actionType: ActionType;
	children?: ReactNode;
	onClick: () => void;
};

export const ActionItemOld = ({ actionType, children, onClick }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			'issueTableHeaderActions',
			{ actionType },
		);

		onClick();
	}, [actionType, createAnalyticsEvent, onClick]);

	return <DropdownItem onClick={handleOnClick}>{children}</DropdownItem>;
};

export const ActionItemNew = forwardRef(
	({ actionType, children, onClick }: Props, ref: Ref<HTMLElement>) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleOnClick = useCallback(() => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'dropdownItem',
				}),
				'issueTableHeaderActions',
				{ actionType },
			);

			onClick();
		}, [actionType, createAnalyticsEvent, onClick]);

		return (
			<DropdownItem ref={ref} onClick={handleOnClick}>
				{children}
			</DropdownItem>
		);
	},
);

export const ActionItem = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	ActionItemNew,
	ActionItemOld,
);
